import Joyride, {
  TooltipRenderProps,
  Step,
  STATUS,
  CallBackProps,
  ACTIONS,
  Status,
  Actions,
} from 'react-joyride';
import ButtonComponent from './button/buttonComponent';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron-right-current.svg';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds } from 'utilities/constants';
import { twJoin, twMerge } from 'tailwind-merge';
import { useIsMobile } from 'hooks/useIsMobile';
import { useLocation, useNavigate } from 'react-router-dom';
import useUserValidation from 'hooks/useUserValidation';
import {
  AppTourTypes,
  useFinishTourMutation,
  useGetAppToursQuery,
} from 'graphql/generated/hasura';
import { PATIENT_PROFILE_ROUTES } from '../app/my-account/pages/patient/constants';
import { MY_ACCOUNT_PROFILE } from '../utilities/routes';
import { useCustomToast } from '../layout/useToast';

export const CustomTourTooltip: React.FC<TooltipRenderProps> = (props) => {
  const {
    continuous,
    index,
    primaryProps,
    skipProps,
    step,
    size,
    isLastStep,
    backProps,
    tooltipProps,
  } = props;
  const { data: locale } = useGetComponent({
    locale: 'en',
    componentId: componentIds.DASHBOARD_TOUR,
  });

  return (
    <div
      className={twJoin(
        'bg-white w-full max-w-[500px] p-30 -mt-2.5 -ml-2.5 rounded-md',
        step?.data?.className,
      )}
      {...tooltipProps}
    >
      {step.title && (
        <h4 className="text-xl font-semibold text-btn-black">{step.title}</h4>
      )}
      <div className="text-base mt-2 font-medium  text-btn-black">
        {step.content}
      </div>
      <div className="flex w-full mt-5 items-center">
        <p
          className={twMerge(
            'mr-auto text-base text-med-gray-3 font-medium min-w-[70px]',
            step?.data?.hiddenProgress && 'hidden',
          )}
        >
          {index + 1}/{size} {locale?.step}
        </p>
        {!isLastStep && (
          <ButtonComponent
            testID="skip"
            type={'underline'}
            className={'mr-6 !text-sm'}
            {...skipProps}
          >
            {locale?.skip}
          </ButtonComponent>
        )}
        {index > 0 && (
          <ButtonComponent
            testID="prev-step"
            className={'!text-sm !w-auto mr-3 '}
            {...backProps}
            Icon={ChevronRightIcon}
            iconPosition="left"
            iconWidth="w-[6px] rotate-180"
            iconHeight="h-[12px]"
          >
            {backProps.title}
          </ButtonComponent>
        )}
        {continuous && (
          <ButtonComponent
            testID={isLastStep ? 'finish-step' : 'next-step'}
            className={'!text-sm !w-auto'}
            {...primaryProps}
            Icon={ChevronRightIcon}
            iconPosition="right"
            iconWidth={twMerge('w-[6px]', isLastStep && 'hidden')}
            iconHeight="h-[12px]"
          >
            {isLastStep
              ? step?.data?.finish ?? locale?.finish
              : step?.data?.next ?? locale?.next}
          </ButtonComponent>
        )}
      </div>
    </div>
  );
};

const Tour: React.FC<{
  steps: Array<Step>;
  allowOnceByName: AppTourTypes;
  skipValidate?: boolean;
  scrollOffset?: number;
}> = ({ steps, allowOnceByName, skipValidate = false, scrollOffset = 150 }) => {
  const [showTour, setShowTour] = useState(false);
  const isMobile = useIsMobile();
  const { addToast } = useCustomToast();
  const location = useLocation();
  const navigate = useNavigate();
  const previousPage = location.state?.from;

  const isFinishingRef = useRef(false);

  const {
    data: tours,
    refetch,
    loading,
  } = useGetAppToursQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [finishTour] = useFinishTourMutation({
    context: {
      ignoreGlobalErrors: true,
    },
    variables: {
      appTourType: allowOnceByName,
    },
    onCompleted: () => {
      if (previousPage && previousPage === PATIENT_PROFILE_ROUTES.APP_TOUR) {
        addToast({ type: 'success', message: 'Tour is finished' });
        navigate(`${MY_ACCOUNT_PROFILE}/${PATIENT_PROFILE_ROUTES.APP_TOUR}`, {
          replace: true,
        });
      }
    },
    onError: (error) => addToast({ type: 'warning', message: error.message }),
  });

  const finishTours = useMemo(
    () =>
      tours?.getAppTours.app_tours
        ?.map(({ type }) => type)
        ?.includes(allowOnceByName),
    [tours, allowOnceByName],
  );

  const { userValidation } = useUserValidation(true);

  useEffect(() => {
    if (skipValidate) {
      setShowTour(loading ? false : !finishTours);
    } else {
      userValidation()
        .then(({ generalValidation }) => {
          setShowTour(loading ? false : !finishTours && generalValidation);
        })
        .catch((error) => {
          console.error(error);
          setShowTour(false);
        });
    }
  }, [skipValidate, loading, allowOnceByName, finishTours, userValidation]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action } = data;

    if (
      ([STATUS.FINISHED, STATUS.SKIPPED] as Status[]).includes(status) ||
      action === ACTIONS.CLOSE
    ) {
      if (isFinishingRef.current) return;
      isFinishingRef.current = true;

      finishTour()
        .then(() => refetch())
        .then(() => setShowTour(false))
        .catch((err) => {
          setShowTour(false);
          console.error(err);
        });
    }
  };

  if (!showTour || isMobile) return null;

  return (
    <Joyride
      callback={handleJoyrideCallback}
      steps={steps}
      run={true}
      disableScrollParentFix={false}
      scrollOffset={scrollOffset}
      showSkipButton
      continuous
      disableOverlayClose
      tooltipComponent={CustomTourTooltip}
      styles={{
        options: {
          arrowColor: 'transparent',
          zIndex: 10000,
        },
        overlay: {
          zIndex: 9999,
        },
        tooltipContainer: {
          zIndex: 10001,
        },
      }}
    />
  );
};

export const MultiPageTour: React.FC<{
  steps: Array<Step>;
  allowOnceByName: AppTourTypes;
}> = ({ steps, allowOnceByName }) => {
  const navigate = useNavigate();
  const [showTour, setShowTour] = useState(false);
  const [run, setRun] = useState(true);
  const isMobile = useIsMobile();
  const { userValidation } = useUserValidation(true);

  const isFinishingRef = useRef(false);

  const { addToast } = useCustomToast();
  const location = useLocation();
  const previousPage = location.state?.from;

  const {
    data: tours,
    refetch,
    loading,
  } = useGetAppToursQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [finishTour] = useFinishTourMutation({
    variables: {
      appTourType: allowOnceByName,
    },
    onCompleted: () => {
      if (previousPage && previousPage === PATIENT_PROFILE_ROUTES.APP_TOUR) {
        addToast({ type: 'success', message: 'Tour is finished' });
        navigate(`${MY_ACCOUNT_PROFILE}/${PATIENT_PROFILE_ROUTES.APP_TOUR}`, {
          replace: true,
        });
      }
    },
    onError: (error) => addToast({ type: 'warning', message: error.message }),
  });

  const finishTours = useMemo(
    () =>
      tours?.getAppTours.app_tours
        ?.map(({ type }) => type)
        ?.includes(allowOnceByName),
    [allowOnceByName, tours],
  );

  useEffect(() => {
    userValidation()
      .then(({ generalValidation }) => {
        setShowTour(loading ? false : !finishTours && generalValidation);
      })
      .catch((error) => {
        console.error(error);
        setShowTour(false);
      });
  }, [loading, allowOnceByName, finishTours, userValidation]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action, step } = data;
    const {
      data: { next, previous },
    } = step;

    const isPreviousAction = action === ACTIONS.PREV;

    if (([ACTIONS.PREV, ACTIONS.NEXT] as Actions[]).includes(action)) {
      setRun(false);
      navigate(isPreviousAction && previous ? previous : next);
      setTimeout(() => setRun(true), 0);
    }

    if (
      ([STATUS.FINISHED, STATUS.SKIPPED] as Status[]).includes(status) ||
      action === ACTIONS.CLOSE
    ) {
      if (isFinishingRef.current) return;
      isFinishingRef.current = true;

      finishTour()
        .then(() => refetch())
        .then(() => setShowTour(false))
        .catch((err) => {
          setShowTour(false);
          console.error(err);
        });
    }
  };

  if (!showTour || isMobile) return null;

  return (
    <Joyride
      callback={handleJoyrideCallback}
      steps={steps}
      run={run}
      showSkipButton
      continuous
      disableOverlayClose
      tooltipComponent={CustomTourTooltip}
      styles={{
        options: {
          arrowColor: 'transparent',
        },
      }}
    />
  );
};

export default Tour;
